import type {
  FlowMeta,
  LoaderErrorCode,
  ModuleRef,
  TinkertoyErrorInfo,
  TraceIdSource,
} from "@bestowinc/tinkertoy-core";

import type { ModuleLoadError } from "../module-loader";

/**
 * Machine-readable failure code carried by a {@link ShellLoaderError}.
 *
 * Extends the server's {@link LoaderErrorCode} with client-only synthetic
 * codes emitted when the loader cannot even parse what the server returned.
 */
export type ShellLoaderErrorCode =
  | LoaderErrorCode
  | "non_json_response"
  | "invalid_response_shape"
  | "network_error";

/**
 * Structured context about what failed. Mirrors the server's
 * {@link LoaderErrorResponse#errorContext} and is passed straight through to
 * the flow's error-view module as the `error.type` / `error.moduleId` props.
 */
export interface ShellLoaderErrorContext {
  errorType: string;
  moduleId?: string;
}

/**
 * Server trace fields. When `traceId` is absent, a browser reference is
 * generated; the server omits `stackTrace` in production.
 */
export interface ShellLoaderErrorInit
  extends Pick<TinkertoyErrorInfo, "traceId" | "traceIdSource" | "stackTrace"> {
  message: string;
  status: number;
  code: ShellLoaderErrorCode;
  errorView?: ModuleRef;
  errorContext?: ShellLoaderErrorContext;
  flowMeta?: FlowMeta;
  /** Result of the errorView module's server-side `dataFetcher`, if any. */
  errorModuleData?: unknown;
}

/**
 * Random reference for failures with no server trace ID, so the error page
 * and the failure report share an ID support can search for.
 */
function createFallbackTraceId(): string {
  if (typeof globalThis.crypto?.randomUUID === "function") {
    return globalThis.crypto.randomUUID();
  }
  // randomUUID needs a secure context; plain-http hosts land here.
  return `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

/**
 * Typed error thrown by the shell loader (and in the future, by the
 * view renderer's module-load error boundary) when rendering the normal
 * view is not possible.
 *
 * React Router catches the throw and routes the request to the shell's
 * `errorElement` ({@link ShellErrorBoundary}), which uses `errorView` +
 * `errorContext` to render the flow's configured error-view module.
 */
export class ShellLoaderError extends Error {
  readonly status: number;
  readonly code: ShellLoaderErrorCode;
  readonly errorView?: ModuleRef;
  readonly errorContext?: ShellLoaderErrorContext;
  readonly flowMeta?: FlowMeta;
  readonly errorModuleData?: unknown;
  readonly traceId: string;
  readonly traceIdSource: TraceIdSource;
  readonly stackTrace?: string;

  constructor(init: ShellLoaderErrorInit) {
    super(init.message);
    this.name = "ShellLoaderError";
    this.status = init.status;
    this.code = init.code;
    this.errorView = init.errorView;
    this.errorContext = init.errorContext;
    this.flowMeta = init.flowMeta;
    this.errorModuleData = init.errorModuleData;
    if (init.traceId) {
      this.traceId = init.traceId;
      this.traceIdSource = init.traceIdSource ?? "fallback";
    } else {
      this.traceId = createFallbackTraceId();
      this.traceIdSource = "fallback";
    }
    this.stackTrace = init.stackTrace;
  }

  static isInstance(err: unknown): err is ShellLoaderError {
    return err instanceof ShellLoaderError;
  }
}

/**
 * Translate a {@link ModuleLoadError} from the client-side module loader
 * into a {@link ShellLoaderError} with `code: "module_load_failed"` so the
 * shell's error boundary renders the flow's configured error view.
 *
 * Forward-compat helper — reserved for a future `ViewRenderer` module-load
 * boundary (FEP2-????). No call sites today.
 */
export function throwShellLoaderError(
  moduleLoadError: ModuleLoadError,
  errorView?: ModuleRef,
): never {
  throw new ShellLoaderError({
    message: `Failed to load module "${moduleLoadError.moduleId}" (${moduleLoadError.code})`,
    status: 500,
    code: "module_load_failed",
    errorView,
    errorContext: {
      errorType: "module_load_failure",
      moduleId: moduleLoadError.moduleId,
    },
  });
}
